<template>
  <div
    class="box box-padding bg-gradient-to-r relative logo-background"
    :class="boxColor"
  >
    <div class="relative z-20">
      <h2 class="text-white font-semibold text-lg md:text-xl lg:text-2xl">
        {{ title }}
      </h2>
      <p class="text-white mb-20 md:text-md lg:text-lg">{{ lead }}</p>

      <div class="flex gap-10">
        <ButtonStyle v-slot="{ classes }" v-bind="buttonAttributes" outlined>
          <button :class="classes" @click.prevent="() => paywallAction()">
            {{ buttonText }}
          </button>
        </ButtonStyle>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useDrupalUser } from '~/stores/drupalUser'

const route = useRoute()
const router = useRouter()
const drupalUser = useDrupalUser()
const language = useCurrentLanguage()
const { $texts } = useNuxtApp()

const buttonText = computed(() => {
  return drupalUser.isLoggedIn
    ? $texts('loginWithAnotherAccount', 'Mit anderem Konto anmelden')
    : $texts('register', 'Anmelden')
})

const props = withDefaults(
  defineProps<{
    showPaywall?: boolean
    requiredRoles?: string[]
    destination?: string
    hideFeatures?: boolean
  }>(),
  {
    requiredRoles: () => [],
    destination: '',
  },
)

const targetRole = computed<string>(() => {
  return props.requiredRoles[0] || ''
})

const computedDestination = computed(() => {
  // When the paywall is used for media we show the paywall on a dedicated
  // route (as there is no media canonical) and so pass the destination in the
  // props. If we are showing the paywall on a node page we use the current
  // route path as the destination to redirect to after login.
  return props.destination || route.path
})

const forAffiliatedPharmacy = computed(() => {
  return props.requiredRoles.includes('affiliated_pharmacy')
})

// The logic does not consider the case of a restriction for a non-affiliated
// pharmacy.
const boxColor = computed(() => {
  return forAffiliatedPharmacy.value
    ? 'from-berry to-pink'
    : 'from-orange-light to-orange'
})

const buttonAttributes = computed(() => {
  return forAffiliatedPharmacy.value ? { berry: true } : { orange: true }
})

const title = computed(() => {
  return forAffiliatedPharmacy.value
    ? $texts(
        'paywallContent.TitlePharmacy',
        'Informationen für angeschlossene Apotheken.',
      )
    : $texts(
        'paywallContent.TitleMember',
        'Informationen für Einzelmitglieder.',
      )
})

const lead = computed(() => {
  return forAffiliatedPharmacy.value
    ? $texts(
        'paywallContent.TextPharmacy',
        'Bitte melden Sie sich als angeschlossene Apotheke an, um auf diesen Inhalt zuzugreifen.',
      )
    : $texts(
        'paywallContent.TextMember',
        'Bitte melden Sie sich als Einzelmitglied an, um auf diesen Inhalt zuzugreifen.',
      )
})

async function paywallAction() {
  // We need to redirect to the login page with the relevant role
  // and the destination. If the user is logged in we first need to log them
  // out.
  const query = {
    role: targetRole.value,
    destination: computedDestination.value,
  }
  if (drupalUser.isLoggedIn) {
    // We need to first logout the user (we don't redirect at this point).
    const destination = router.resolve({
      path: `/${language.value}/user/login`,
      query,
    })
    // We redirect the user to the destination after logging them out.
    await drupalUser.logout(destination.href)
  } else {
    // Redirect to the login page with the relevant role (if applicable)
    // and the destination.
    router.push({
      name: 'login',
      query,
    })
  }
}
</script>
